import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Send, Paperclip, X, FileText, CheckCircle, Building2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const categories = [
  'BPM / Flow Ürünleri', 'Logo Tiger / Go Ürünleri', 'Bordro Ürünleri',
  'E-Devlet Ürünleri', 'Özel Yazılım ve Servis', 'Genel'
];

export default function CustomerSupportPage() {
  const { token } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [identified, setIdentified] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'Genel', priority: 'Normal' });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Clipboard paste support
  useEffect(() => {
    const handlePaste = (e) => {
      if (step !== 2) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const named = new File([file], `screenshot-${Date.now()}.png`, { type: file.type });
            setFiles(prev => [...prev, named].slice(0, 5));
            toast.success('Ekran görüntüsü eklendi!');
          }
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [step]);

  useEffect(() => {
    api.get(`/public/support/${token}`)
      .then(res => { setCustomer(res.data); setLoading(false); })
      .catch(() => { setError('Geçersiz destek linki.'); setLoading(false); });
  }, [token]);

  const handleIdentify = useCallback(async () => {
    if (!email || !email.includes('@')) return;
    try {
      const res = await api.post(`/public/support/${token}/identify`, { email });
      if (res.data.found) {
        setFullName(res.data.FullName || '');
        setIdentified(true);
        toast.success(`Hoş geldiniz, ${res.data.FullName}!`);
      }
    } catch (err) {}
  }, [email, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !fullName || !form.title) {
      toast.error('Lütfen gerekli alanları doldurun!');
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('email', email);
      formData.append('fullName', fullName);
      formData.append('type', 'Destek');
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('category', form.category);
      formData.append('priority', form.priority);
      files.forEach(f => formData.append('files', f));

      const res = await api.post(`/public/support/${token}/request`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Talep oluşturulamadı');
    }
    setSending(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-12 text-center max-w-md">
        <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-800 mb-2">Geçersiz Link</h2>
        <p className="text-slate-500">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="DSY" className="h-10" />
            <div>
              <h1 className="text-lg font-bold text-slate-800">DSY Destek Sistemi</h1>
              <p className="text-xs text-slate-500">Hızlı talep oluşturma</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Building2 size={16} className="text-blue-600" />
            <span className="font-semibold">{customer.Name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        {step < 3 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2].map(s => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step >= s ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                }`}>{s}</div>
                {s < 2 && <div className={`w-12 h-1 rounded transition-all ${step > s ? 'bg-blue-600' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Email */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Merhaba!</h2>
            <p className="text-slate-500 mb-6">Talebinizi hızlıca oluşturmak için bilgilerinizi girin.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">E-posta Adresiniz *</label>
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); if (identified) { setIdentified(false); setFullName(''); } }} onBlur={handleIdentify} required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg" placeholder="ornek@firma.com" />
                {identified && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><CheckCircle size={12} /> Tanındınız!</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ad Soyad *</label>
                <input type="text" value={fullName} onChange={e => { if (!identified) setFullName(e.target.value); }} readOnly={identified} required
                  className={`w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg ${identified ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`}
                  placeholder="Ad Soyad" />
              </div>
              <button onClick={() => { if (email && fullName) setStep(2); else toast.error('E-posta ve ad soyad zorunludur!'); }}
                className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors">
                Devam Et →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Details */}
        {step === 2 && (
          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 space-y-5">
              <h2 className="text-2xl font-bold text-slate-800">Talep Detayları</h2>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Konu *</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Kısa bir başlık" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Açıklama</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={5}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[120px]" placeholder="Detaylı açıklama..." />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Kategori</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {/* Files */}
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-1.5"><Paperclip size={14} /> Dosya Ekle</label>
                <div className="flex items-center gap-3 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg flex-shrink-0">
                    <span className="text-blue-600 text-xs font-bold">⌨️</span>
                  </div>
                  <p className="text-xs text-blue-700"><strong>İpucu:</strong> Ekran görüntüsü almak için <kbd className="px-1.5 py-0.5 bg-blue-200 rounded text-[10px] font-mono">Ctrl+V</kbd> yaparak doğrudan yapıştırabilirsiniz!</p>
                </div>
                <input type="file" multiple onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 5))} className="text-sm text-slate-500" />
                {files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 p-2 rounded-lg">
                        <FileText size={14} className="text-slate-400" />
                        <span className="text-slate-700 flex-1 truncate">{f.name}</span>
                        <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="px-6 py-3 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50">← Geri</button>
                <button type="submit" disabled={sending}
                  className="flex-1 py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {sending ? 'Gönderiliyor...' : <><Send size={18} /> Talep Oluştur</>}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Step 3: Success */}
        {step === 3 && result && (
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-10 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Talebiniz Oluşturuldu!</h2>
            <p className="text-slate-500 mb-4">Talep numaranız: <strong>#{result.ticketId}</strong></p>
            <p className="text-sm text-slate-400 mb-6">E-posta adresinize takip linki gönderildi.</p>
            {result.trackingUrl && (
              <Link to={result.trackingUrl}
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors">
                Talebi Takip Et →
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
